import { LOSS_REASONS, formatPaymentTerms, PAYMENT_TERM_PRESETS, PAYMENT_TERMS_NOT_STATED } from '@crm/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorText } from '../lib/api';
import { formatMoney, riyadhToday } from '../lib/format';
import { toast } from '../lib/motion';

type Action = 'sent' | 'plus30' | 'plus60' | 'lost' | 'reason' | 'call' | 'done' | 'tomorrow' | 'amount' | 'closeDate' | 'terms' | 'gp' | 'quoteDate' | 'directPo' | 'waiting';
type FormAction = 'lost' | 'reason' | 'call' | 'amount' | 'closeDate' | 'terms' | 'gp' | 'quoteDate' | 'waiting';
interface PeekItem {
  id: string; kind: 'deal' | 'task'; name: string; sub: string | null; amount: number | null; detail: string | null; to: string; actions: Action[]; owner?: string | null;
  closeDate?: string | null; stageId?: string | null; lostStageId?: string | null; dueDate?: string | null; status?: string; version?: number; quoteSubmittedDate?: string | null;
}

const today = riyadhToday;
const addDays = (ymd: string, n: number) => new Date(Date.parse(`${ymd}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const errMsg = (e: unknown) => errorText(e, 'Could not save.');
/** "SAR 6.4M", "SAR 950K": the value at stake on a card. */
// Rounded first (999,600 is "SAR 1.0M", not "SAR 1000K"), and negatives shortened the same way.
export const compactSar = (v: number) => {
  const a = Math.abs(v);
  const k = Math.round(a / 1e3);
  if (a >= 1e6 || k >= 1000) return `SAR ${(v / 1e6).toFixed(a >= 9.95e6 ? 0 : 1)}M`;
  return a >= 1e3 ? `SAR ${Math.sign(v) * k}K` : `SAR ${Math.round(v)}`;
};

const deal = (id: string, body: Record<string, unknown>) => api(`/api/records/opportunity/${id}`, { method: 'PATCH', body, quiet: true });
const task = (id: string, body: Record<string, unknown>) => api(`/api/activities/${id}`, { method: 'PATCH', body, quiet: true });

/**
 * A Home attention card, unfolded: its first items (most urgent first) and the quick fixes allowed on each. A fix goes
 * through the normal APIs (same rights and checks), the item slides out, the card's number counts down, and Undo
 * puts it back for a few seconds. "See all" opens the full list.
 */
export function AttentionPeek({ cardKey, to, total, onFixed }: { cardKey: string; to: string | null; total: number; onFixed: (undo: boolean) => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['attention-peek', cardKey], queryFn: () => api<{ items: PeekItem[]; total: number }>(`/api/attention/peek?key=${encodeURIComponent(cardKey)}`) });
  const [leaving, setLeaving] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState<{ id: string; action: FormAction } | null>(null);
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['attention-peek'] }); void qc.invalidateQueries({ queryKey: ['records'] }); void qc.invalidateQueries({ queryKey: ['my-work'] }); };

  const run = async (it: PeekItem, action: Action) => {
    setBusy(it.id); setErr(null);
    try {
      let undo: () => Promise<unknown>;
      let text: string;
      const d = today();
      if (action === 'sent') { await deal(it.id, { quoteSubmittedDate: d }); undo = () => deal(it.id, { quoteSubmittedDate: null }); text = 'Quotation marked sent today: follow-ups planned'; }
      else if (action === 'plus30' || action === 'plus60') {
        const n = action === 'plus30' ? 30 : 60;
        await deal(it.id, { closeDate: addDays(d, n) }); undo = () => deal(it.id, { closeDate: it.closeDate ?? null }); text = `Close date moved ${n} days ahead`;
      } else if (action === 'lost') {
        await deal(it.id, { stageId: it.lostStageId, primaryLossReason: reason }); undo = () => deal(it.id, { stageId: it.stageId, primaryLossReason: null }); text = 'Marked lost';
      } else if (action === 'reason') {
        await deal(it.id, { primaryLossReason: reason }); undo = () => deal(it.id, { primaryLossReason: null }); text = 'Loss reason saved';
      } else if (action === 'amount') {
        await deal(it.id, { amount: Number(value) }); undo = () => deal(it.id, { amount: it.amount ?? null }); text = 'Amount saved: the forecast counts it now';
      } else if (action === 'quoteDate') {
        // The day the quotation really went out (the win rate and time to PO count on it).
        await deal(it.id, { quoteSubmittedDate: value }); undo = () => deal(it.id, { quoteSubmittedDate: it.quoteSubmittedDate ?? null }); text = 'Quotation date saved';
      } else if (action === 'directPo') {
        await deal(it.id, { quoteSubmittedDate: null }); undo = () => deal(it.id, { quoteSubmittedDate: it.quoteSubmittedDate ?? null }); text = 'Saved: direct PO, no quotation';
      } else if (action === 'gp') {
        // The deal's expected gross margin: used when its quoted brands and quotation have no GP%.
        await deal(it.id, { expectedMargin: Number(value) }); undo = () => deal(it.id, { expectedMargin: null }); text = `GP ${Number(value)}% saved: the CEO page counts it now`;
      } else if (action === 'terms') {
        // One of the common terms, or "Not stated in the PO" (other terms: on the deal).
        const preset = PAYMENT_TERM_PRESETS.find((x) => x.key === value);
        await deal(it.id, { paymentTerms: preset ? formatPaymentTerms(preset.terms) : PAYMENT_TERMS_NOT_STATED }); undo = () => deal(it.id, { paymentTerms: null });
        text = preset ? `Payment terms ${preset.label} saved` : 'Saved: not stated in the PO';
      } else if (action === 'closeDate') {
        // The month the customer expects to decide, saved as its 15th (a month's end would put December back on 31 Dec).
        await deal(it.id, { closeDate: `${value}-15` }); undo = () => deal(it.id, { closeDate: it.closeDate ?? null }); text = 'Expected month saved';
      } else if (action === 'waiting') {
        // Our customer priced to their own client, who hasn't decided: the reminder pauses until the day to ask again.
        const path = `/api/opportunities/${it.id}/close-waiting`;
        await api(path, { method: 'PUT', quiet: true, body: { decider: note.trim() || null, recheckOn: value } });
        undo = () => api(path, { method: 'DELETE', quiet: true }); text = `Waiting for the end client: ask again on ${value}`;
      } else if (action === 'call') {
        const r = await api<{ activity: { id: string } }>('/api/activities', { method: 'POST', quiet: true,
          body: { kind: 'task', subtype: 'call', status: 'completed', dueDate: d, subject: 'Call with the customer', description: note.trim() || null, whatType: 'opportunity', whatId: it.id } });
        undo = () => api(`/api/activities/${r.activity.id}?series=one`, { method: 'DELETE', quiet: true }); text = 'Call logged';
      } else if (action === 'done') {
        await task(it.id, { status: 'completed' }); undo = () => task(it.id, { status: it.status ?? 'not_started' }); text = 'Task done';
      } else {
        await task(it.id, { dueDate: addDays(d, 1) }); undo = () => task(it.id, { dueDate: it.dueDate ?? null }); text = 'Moved to tomorrow';
      }
      setForm(null); setReason(''); setNote(''); setValue('');
      // The item slides out, then the lists refresh (the next item moves up; the card's number counts down).
      setLeaving((s) => new Set(s).add(it.id));
      window.setTimeout(refresh, 380);
      onFixed(false);
      toast(text, { undo: () => undo().then(() => { setLeaving((s) => { const n = new Set(s); n.delete(it.id); return n; }); refresh(); onFixed(true); }) });
    } catch (e) {
      setErr(errMsg(e));
    }
    setBusy(null);
  };

  const items = q.data?.items ?? [];
  return (
    <div className="att-peek" role="region" aria-label="First items" onClick={(e) => e.stopPropagation()}>
      {q.isPending ? <p className="loading-line small">Loading…</p> : !items.length ? <p className="muted small">Nothing left here.</p> : (
        <ul className="att-peek-list">
          {items.map((it) => (
            <li key={it.id} className={leaving.has(it.id) ? 'leaving' : ''}>
              <div className="att-peek-main">
                <Link to={it.to} className="att-peek-name">{it.name}</Link>
                <span className="small muted">{[it.sub, it.amount != null ? formatMoney(it.amount, 'SAR') : null, it.detail].filter(Boolean).join(' · ')}</span>
                {it.owner && <span className="att-peek-owner small">{it.owner.split(' ')[0]}’s deal</span>}
              </div>
              {it.actions.length > 0 && form?.id !== it.id && (
                <div className="att-peek-actions">
                  {it.actions.map((a) => {
                    const label = { sent: 'Mark sent today', plus30: '+30 days', plus60: '+60 days', lost: 'Lost', reason: 'Add reason', call: 'Log a call', done: '✓ Done', tomorrow: 'Tomorrow',
                      amount: 'Set amount', closeDate: 'Set expected month', waiting: 'Waiting for the end client…', terms: 'Set terms', gp: 'Enter GP%', quoteDate: 'Set the real date', directPo: 'Direct PO' }[a];
                    const needsForm = a === 'lost' || a === 'reason' || a === 'call' || a === 'amount' || a === 'closeDate' || a === 'terms' || a === 'gp' || a === 'quoteDate' || a === 'waiting';
                    return (
                      <button key={a} type="button" className={`btn small${a === 'sent' || a === 'done' ? ' primary' : ''}`} disabled={busy === it.id}
                        onClick={() => { if (!needsForm) { void run(it, a); return; } setValue(a === 'waiting' ? addDays(today(), 30) : ''); setNote(''); setForm({ id: it.id, action: a as FormAction }); }}>{label}</button>
                    );
                  })}
                  {it.actions.includes('plus30') && <Link className="btn small ghost" to={it.to} title="Winning needs the PO and the win details">Won…</Link>}
                </div>
              )}
              {form?.id === it.id && (
                <form className="att-peek-form" onSubmit={(e) => { e.preventDefault(); void run(it, form.action); }}>
                  {form.action === 'amount' ? (
                    <input type="number" inputMode="decimal" min={1} step="any" required autoFocus value={value} onChange={(e) => setValue(e.target.value)} placeholder="Expected value (SAR)" aria-label="Expected value in SAR" />
                  ) : form.action === 'quoteDate' ? (
                    <input type="date" required autoFocus max={today()} value={value} onChange={(e) => setValue(e.target.value)} aria-label="Quotation sent on" />
                  ) : form.action === 'gp' ? (
                    <input type="number" inputMode="decimal" min={-100} max={100} step="any" required autoFocus value={value} onChange={(e) => setValue(e.target.value)} placeholder="Gross profit % of this deal" aria-label="Gross profit percent" />
                  ) : form.action === 'terms' ? (
                    <select required autoFocus value={value} onChange={(e) => setValue(e.target.value)} aria-label="Payment terms">
                      <option value="">Payment terms of the PO…</option>
                      {PAYMENT_TERM_PRESETS.map((x) => <option key={x.key} value={x.key}>{x.label} (advance by PI)</option>)}
                      <option value="none">{PAYMENT_TERMS_NOT_STATED}</option>
                    </select>
                  ) : form.action === 'closeDate' ? (
                    <label className="att-peek-field"><span className="small muted">Expected month of the decision</span>
                      <input type="month" required autoFocus min={today().slice(0, 7)} value={value} onChange={(e) => setValue(e.target.value)} aria-label="Expected month of the decision" /></label>
                  ) : form.action === 'waiting' ? (
                    <div className="att-peek-waiting">
                      <p className="small muted">Our customer sent the price to their own client, who hasn’t decided yet. The close date stays an estimate and the deal stays in the forecast.</p>
                      <label className="att-peek-field"><span className="small muted">Who decides? (optional)</span>
                        <input type="text" maxLength={200} autoFocus value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. the owner, Rua Al Madinah" dir="auto" /></label>
                      <label className="att-peek-field"><span className="small muted">Ask the customer again on</span>
                        <input type="date" required min={addDays(today(), 1)} value={value} onChange={(e) => setValue(e.target.value)} /></label>
                    </div>
                  ) : form.action === 'call' ? (
                    <textarea rows={2} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What did the customer say? (optional)" dir="auto" autoFocus />
                  ) : (
                    <select value={reason} onChange={(e) => setReason(e.target.value)} required autoFocus aria-label="Loss reason">
                      <option value="">Why was it lost?</option>
                      {LOSS_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  )}
                  <button className="btn small primary" disabled={busy === it.id || ((form.action === 'lost' || form.action === 'reason') && !reason) || ((form.action === 'amount' || form.action === 'closeDate' || form.action === 'terms' || form.action === 'gp' || form.action === 'quoteDate' || form.action === 'waiting') && !value)}>
                    {form.action === 'call' ? 'Save call' : form.action === 'lost' ? 'Mark lost' : 'Save'}</button>
                  <button type="button" className="btn small ghost" onClick={() => setForm(null)}>Cancel</button>
                </form>
              )}
            </li>
          ))}
        </ul>
      )}
      {err && <p className="form-error small">{err}</p>}
      {to && <Link className="att-peek-all" to={to}>See all {Math.max(total, q.data?.total ?? 0)} →</Link>}
    </div>
  );
}
